import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, ArrowUpRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { NEWSLETTER } from '@/constants/testIds';
import { subscribeNewsletter } from '@/lib/api';
import { toast } from 'sonner';

export default function Footer() {
  const [email, setEmail] = React.useState('');
  const [loading, setLoading] = React.useState(false);

  const onSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await subscribeNewsletter(email);
      toast.success('Subscribed. Welcome to TITO Insights.');
      setEmail('');
    } catch {
      toast.error('Could not subscribe. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-[#001E3D] text-slate-200 mt-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12">
          <div className="md:col-span-5">
            <div className="flex items-center gap-2.5">
              <span className="relative inline-flex h-10 w-10 items-center justify-center rounded-sm bg-white text-tito-blue font-display font-extrabold text-lg">
                T
                <span className="absolute -bottom-0.5 -right-0.5 h-2 w-2 bg-tito-gold rounded-sm" />
              </span>
              <div>
                <div className="font-display font-bold text-white text-base">TITO Business Growth</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400">A division of TITO Corporate Services Provider</div>
              </div>
            </div>
            <p className="mt-6 text-sm text-slate-300/80 max-w-md leading-relaxed">
              Empowering businesses through smart workforce, business growth and corporate solutions across the GCC.
            </p>

            <form onSubmit={onSubscribe} className="mt-8 flex flex-col sm:flex-row gap-2 max-w-md">
              <Input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email for insights"
                data-testid={NEWSLETTER.email}
                className="bg-white/5 border-white/10 text-white placeholder:text-slate-400 rounded-sm h-11"
              />
              <Button
                type="submit"
                disabled={loading}
                data-testid={NEWSLETTER.submit}
                className="bg-tito-gold hover:bg-yellow-500 text-[#001E3D] rounded-sm h-11 px-5 gap-1.5 font-semibold"
              >
                {loading ? 'Joining…' : 'Subscribe'}
                <ArrowUpRight className="h-4 w-4" />
              </Button>
            </form>
          </div>

          <div className="md:col-span-3">
            <div className="eyebrow text-tito-gold">Navigate</div>
            <ul className="mt-5 space-y-2.5 text-sm">
              <li><Link to="/" className="text-slate-300 hover:text-white transition-colors">Home</Link></li>
              <li><Link to="/about" className="text-slate-300 hover:text-white transition-colors">About Us</Link></li>
              <li><Link to="/services" className="text-slate-300 hover:text-white transition-colors">Services</Link></li>
              <li><Link to="/industries" className="text-slate-300 hover:text-white transition-colors">Industries</Link></li>
              <li><Link to="/why-choose-us" className="text-slate-300 hover:text-white transition-colors">Why Choose Us</Link></li>
              <li><Link to="/insights" className="text-slate-300 hover:text-white transition-colors">Insights</Link></li>
              <li><Link to="/contact" className="text-slate-300 hover:text-white transition-colors">Contact</Link></li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <div className="eyebrow text-tito-gold">Contact</div>
            <ul className="mt-5 space-y-4 text-sm">
              <li className="flex items-start gap-3">
                <MapPin className="h-4 w-4 text-tito-gold mt-0.5 shrink-0" />
                <span className="text-slate-300">101, Ibrahim Hussain Ahli Tower, Muteena, Deira, Dubai</span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="h-4 w-4 text-tito-gold shrink-0" />
                <a href="tel:+971558805477" className="text-slate-300 hover:text-white">+971 55 880 5477</a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="h-4 w-4 text-tito-gold shrink-0" />
                <a href="mailto:titocorporateserviceprovider@gmail.com" className="text-slate-300 hover:text-white break-all">titocorporateserviceprovider@gmail.com</a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-slate-400">© {new Date().getFullYear()} TITO Business Growth & Workforce Solutions. All rights reserved.</p>
          <p className="text-xs text-slate-400">Dubai, United Arab Emirates</p>
        </div>
      </div>
    </footer>
  );
}
